import React from 'react';
import { Trash2, Tag, Info, Flame } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface PriceBlockData {
  originalPrice: string;
  salePrice: string;
  currency: string;
  discount: string;
  promotionText: string;
  badgeColor?: string;
  priceColor?: string;
  discountColor?: string;
}

interface PriceBlockProps {
  data: PriceBlockData;
  onChange: (data: PriceBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const DEFAULT_BADGE_COLOR = '#f97316';
const DEFAULT_PRICE_COLOR = '#ea580c';
const DEFAULT_DISCOUNT_COLOR = '#ef4444';

function hexToRgba(hex: string, alpha: number): string {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return `rgba(249,115,22,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function shade(hex: string, factor: number): string {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return hex;
  const ch = (i: number) => Math.max(0, Math.round(parseInt(clean.slice(i, i + 2), 16) * (1 - factor)));
  return `rgb(${ch(0)},${ch(2)},${ch(4)})`;
}

const PriceBlock: React.FC<PriceBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const badgeColor = data.badgeColor || DEFAULT_BADGE_COLOR;
  const priceColor = data.priceColor || DEFAULT_PRICE_COLOR;
  const discountColor = data.discountColor || DEFAULT_DISCOUNT_COLOR;
  // The badge renders "ลด {N}%" itself — strip any "ลด"/"%" the user (or AI) typed into the field
  // so it never shows doubled as "ลด ลด 33%%".
  const discountText = String(data.discount || '').replace(/ลด|%|\s/g, '');
  const origNum = parseFloat(String(data.originalPrice || '').replace(/[^\d.]/g, ''));
  const saleNum = parseFloat(String(data.salePrice || '').replace(/[^\d.]/g, ''));
  const savings = origNum > 0 && saleNum >= 0 && origNum > saleNum ? origNum - saleNum : 0;

  if (isPreview) {
    return (
      <div
        className="relative overflow-hidden rounded-3xl px-6 pt-7 pb-6 text-center text-white max-w-md mx-auto"
        style={{ background: `linear-gradient(135deg, ${badgeColor} 0%, ${shade(badgeColor, 0.35)} 100%)`, boxShadow: `0 24px 50px -20px ${hexToRgba(badgeColor, 0.6)}` }}
      >
        <style>{`
          @keyframes spShine { 0% { transform: translateX(-180%) rotate(18deg); } 55%, 100% { transform: translateX(300%) rotate(18deg); } }
          @keyframes spRibbon { 0%, 100% { transform: rotate(-2deg) scale(1); } 50% { transform: rotate(-2deg) scale(1.09); } }
          @keyframes spFlame { 0%, 100% { transform: scale(1) rotate(-4deg); } 50% { transform: scale(1.25) rotate(5deg); } }
        `}</style>

        {/* soft glow decorations */}
        <div className="absolute -top-12 -right-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)' }} />
        <div className="absolute -bottom-14 -left-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent 70%)' }} />

        {/* moving shine sweep */}
        <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none" style={{ background: 'linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent)', animation: 'spShine 3.2s ease-in-out infinite' }} />

        {/* corner discount burst */}
        {discountText && (
          <div
            className="absolute top-3.5 -right-1.5 px-4 py-1.5 rounded-l-full font-extrabold text-sm text-white"
            style={{ backgroundColor: discountColor, boxShadow: `0 8px 20px -8px ${hexToRgba(discountColor, 0.9)}`, animation: 'spRibbon 1.8s ease-in-out infinite' }}
          >
            ลด {discountText}%
          </div>
        )}

        {data.promotionText && (
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold mb-2.5 bg-white/15 border border-white/25 backdrop-blur-sm">
            <Tag className="w-3.5 h-3.5" />
            {data.promotionText}
          </div>
        )}

        {data.originalPrice && (
          <div className="text-base line-through text-white/60 font-medium">
            ราคาปกติ {data.originalPrice} {data.currency}
          </div>
        )}

        <div className="flex items-baseline justify-center gap-2" style={{ textShadow: '0 6px 24px rgba(0,0,0,0.28)' }}>
          <span className="text-7xl md:text-8xl font-extrabold tracking-tight">{data.salePrice}</span>
          <span className="text-3xl md:text-4xl font-bold">{data.currency}</span>
        </div>

        {savings > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full font-extrabold text-base bg-white shadow-lg" style={{ color: shade(badgeColor, 0.15) }}>
            <Flame className="w-5 h-5" style={{ color: discountColor, animation: 'spFlame 1.2s ease-in-out infinite' }} />
            ประหยัดทันที {savings.toLocaleString()} {data.currency}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">ราคาสินค้า</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">แสดงราคาสินค้าอย่างโดดเด่น</p>
              <p className="text-gray-300">แสดงราคาปกติและราคาพิเศษ พร้อมเปอร์เซ็นต์ส่วนลด เพื่อกระตุ้นให้ลูกค้าตัดสินใจซื้อเร็วขึ้น</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ราคาปกติ (ถ้ามี)</label>
          <input
            type="text"
            value={data.originalPrice}
            onChange={(e) => onChange({ ...data, originalPrice: e.target.value })}
            placeholder="999"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ราคาพิเศษ *</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={priceColor}
              fallback={DEFAULT_PRICE_COLOR}
              onChange={(hex) => onChange({ ...data, priceColor: hex })}
            />
            <input
              type="text"
              value={data.salePrice}
              onChange={(e) => onChange({ ...data, salePrice: e.target.value })}
              placeholder="699"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">สกุลเงิน</label>
            <select
              value={data.currency}
              onChange={(e) => onChange({ ...data, currency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="฿">฿ (บาท)</option>
              <option value="$">$ (ดอลลาร์)</option>
              <option value="€">€ (ยูโร)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ส่วนลด (%)</label>
            <div className="flex items-center gap-2">
              <ColorSwatchPicker
                compact
                color={discountColor}
                fallback={DEFAULT_DISCOUNT_COLOR}
                onChange={(hex) => onChange({ ...data, discountColor: hex })}
              />
              <input
                type="text"
                value={data.discount}
                onChange={(e) => onChange({ ...data, discount: e.target.value })}
                placeholder="30"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ข้อความโปรโมชั่น</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={badgeColor}
              fallback={DEFAULT_BADGE_COLOR}
              onChange={(hex) => onChange({ ...data, badgeColor: hex })}
            />
            <input
              type="text"
              value={data.promotionText}
              onChange={(e) => onChange({ ...data, promotionText: e.target.value })}
              placeholder="โปรโมชั่นพิเศษ! ลดสูงสุด"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">ตัวอย่าง:</p>
          <div
            className="relative overflow-hidden rounded-2xl px-6 pt-7 pb-6 text-center text-white"
            style={{ background: `linear-gradient(135deg, ${badgeColor} 0%, ${shade(badgeColor, 0.35)} 100%)`, boxShadow: `0 18px 38px -18px ${hexToRgba(badgeColor, 0.6)}` }}
          >
            <div className="absolute -top-10 -right-8 w-36 h-36 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)' }} />
            {discountText && (
              <div className="absolute top-3 -right-1.5 px-3.5 py-1 rounded-l-full font-extrabold text-xs text-white" style={{ backgroundColor: discountColor, transform: 'rotate(-2deg)' }}>
                ลด {discountText}%
              </div>
            )}
            {data.promotionText && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-2.5 bg-white/15 border border-white/25">
                <Tag className="w-3 h-3" />
                {data.promotionText}
              </div>
            )}
            {data.originalPrice && (
              <div className="text-sm line-through text-white/60">ราคาปกติ {data.originalPrice} {data.currency}</div>
            )}
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-4xl font-extrabold tracking-tight">{data.salePrice || '0'}</span>
              <span className="text-xl font-bold">{data.currency}</span>
            </div>
            {savings > 0 && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-extrabold text-sm bg-white" style={{ color: shade(badgeColor, 0.15) }}>
                <Flame className="w-4 h-4" style={{ color: discountColor }} />
                ประหยัดทันที {savings.toLocaleString()} {data.currency}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceBlock;
